/**
 * Administration → User Groups (VSNP-1161)
 *
 * The TENANT's identity groups — the user groups the ZTNA policy "Who → Groups"
 * matches. Stored as nested Keycloak groups "/t-<tenantId>/<name>", so every
 * tenant manages its own namespace and two tenants may each own an
 * "engineering". Per-tenant CRUD by a TenantAdmin (a SuperAdmin acts on the
 * selected tenant); the route is TenantGuard-ed because the page is meaningless
 * in All-Tenants mode.
 *
 * Members are drawn ONLY from this tenant's VPN-user directory. A group that a
 * policy rule still references cannot be deleted (the API refuses; we surface
 * its reason and disable the button up front).
 */

import { useCallback, useEffect, useState } from 'react';
import { Plus, RefreshCw, Trash2, Users as UsersIcon, UserPlus, X, Link2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import DataTable, { type Column } from '../../components/common/DataTable';
import StatCard from '../../components/common/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Modal from '../../components/common/Modal';
import AccessFormField from '../../components/access/AccessFormField';
import { useToast } from '../../components/common/ToastContainer';
import accessService, {
  type ExternalGroupMapping,
  type IdentityGroup,
  type IdentityGroupMember,
  type IdentityProvider,
} from '../../services/access.service';
import vpnUserService, { type VPNUser } from '../../services/vpnUser.service';

// Mirrors the API's identityGroupNameSchema — no "/" (it becomes a Keycloak path segment).
const NAME_RE = /^[a-z0-9][a-z0-9._-]*$/;

const UserGroups = () => {
  const toast = useToast();

  const [groups, setGroups] = useState<IdentityGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create modal — a single validated field, so plain state rather than RHF.
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNameError, setNewNameError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<IdentityGroup | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Members drawer.
  const [membersFor, setMembersFor] = useState<IdentityGroup | null>(null);
  const [members, setMembers] = useState<IdentityGroupMember[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [directory, setDirectory] = useState<VPNUser[]>([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [busyUserId, setBusyUserId] = useState<string | null>(null);

  // External group links (VSNP-1164): an IdP-side group id → this group, applied
  // by the broker at every login. Only offered when the tenant has a provider.
  const [providers, setProviders] = useState<IdentityProvider[]>([]);
  const [mappings, setMappings] = useState<ExternalGroupMapping[]>([]);
  const [mappingProviderId, setMappingProviderId] = useState('');
  const [externalGroupId, setExternalGroupId] = useState('');
  const [mappingBusy, setMappingBusy] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setGroups(await accessService.listIdentityGroups());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user groups');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // The tenant's own users are the only valid members — a foreign tenant's user
  // is rejected by the API anyway, so we never even offer one.
  const fetchDirectory = useCallback(async () => {
    try {
      const res = await vpnUserService.list({ pageSize: 500 });
      setDirectory(res?.items ?? []);
    } catch {
      setDirectory([]);
    }
    try {
      setProviders(await accessService.listIdentityProviders());
    } catch {
      setProviders([]);
    }
  }, []);

  useEffect(() => {
    void fetchGroups();
    void fetchDirectory();
  }, [fetchGroups, fetchDirectory]);

  const openCreate = () => {
    setNewName('');
    setNewNameError(null);
    setIsCreateOpen(true);
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!NAME_RE.test(name)) {
      setNewNameError('Lowercase letters, digits and . _ - only');
      return;
    }
    try {
      setCreating(true);
      await accessService.createIdentityGroup(name);
      toast.success('Group created', name);
      setIsCreateOpen(false);
      await fetchGroups();
    } catch (err) {
      // 409 from the API = this tenant already has that name.
      toast.error('Create failed', err instanceof Error ? err.message : 'Could not create the group');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await accessService.deleteIdentityGroup(deleteTarget.id);
      toast.success('Group deleted', deleteTarget.name);
      setDeleteTarget(null);
      await fetchGroups();
    } catch (err) {
      // The API refuses while a policy rule references the group — surface why.
      toast.error('Delete failed', err instanceof Error ? err.message : 'Could not delete the group');
    } finally {
      setDeleting(false);
    }
  };

  const openMembers = async (group: IdentityGroup) => {
    setMembersFor(group);
    setMemberQuery('');
    setMembers([]);
    setMappings([]);
    setExternalGroupId('');
    setMappingProviderId(providers[0]?.id ?? '');
    try {
      setMembersLoading(true);
      const [m, x] = await Promise.all([
        accessService.listIdentityGroupMembers(group.id),
        providers.length > 0 ? accessService.listExternalGroupMappings(group.id) : Promise.resolve([]),
      ]);
      setMembers(m);
      setMappings(x);
    } catch (err) {
      toast.error('Could not load members', err instanceof Error ? err.message : 'Load failed');
    } finally {
      setMembersLoading(false);
    }
  };

  const addMapping = async () => {
    if (!membersFor || !mappingProviderId || !externalGroupId.trim()) return;
    try {
      setMappingBusy(true);
      const created = await accessService.addExternalGroupMapping(membersFor.id, {
        identityProviderId: mappingProviderId,
        externalGroupId: externalGroupId.trim(),
      });
      setMappings((x) => [...x, created]);
      setExternalGroupId('');
      toast.success('External group linked', `${created.externalGroupId} → ${membersFor.name}`);
    } catch (err) {
      toast.error('Could not link group', err instanceof Error ? err.message : 'Link failed');
    } finally {
      setMappingBusy(false);
    }
  };

  const removeMapping = async (m: ExternalGroupMapping) => {
    if (!membersFor) return;
    try {
      setMappingBusy(true);
      await accessService.removeExternalGroupMapping(membersFor.id, m.id);
      setMappings((x) => x.filter((y) => y.id !== m.id));
      toast.success('External group unlinked', m.externalGroupId);
    } catch (err) {
      toast.error('Could not unlink group', err instanceof Error ? err.message : 'Unlink failed');
    } finally {
      setMappingBusy(false);
    }
  };

  const addMember = async (user: VPNUser) => {
    if (!membersFor) return;
    setBusyUserId(user.id);
    try {
      setMembers(await accessService.addIdentityGroupMember(membersFor.id, user.id));
      toast.success('Member added', `${user.email} → ${membersFor.name}`);
      await fetchGroups(); // refresh member counts
    } catch (err) {
      toast.error('Could not add member', err instanceof Error ? err.message : 'Add failed');
    } finally {
      setBusyUserId(null);
    }
  };

  const removeMember = async (member: IdentityGroupMember) => {
    if (!membersFor) return;
    setBusyUserId(member.vpnUserId);
    try {
      await accessService.removeIdentityGroupMember(membersFor.id, member.vpnUserId);
      setMembers((m) => m.filter((x) => x.vpnUserId !== member.vpnUserId));
      toast.success('Member removed', `${member.email} ← ${membersFor.name}`);
      await fetchGroups();
    } catch (err) {
      toast.error('Could not remove member', err instanceof Error ? err.message : 'Remove failed');
    } finally {
      setBusyUserId(null);
    }
  };

  const columns: Column<IdentityGroup>[] = [
    {
      key: 'name',
      label: 'Group',
      sortable: true,
      render: (g) => (
        <div>
          <div className="font-medium text-gray-900">{g.name}</div>
          <div className="text-xs text-gray-500 font-mono">{g.path}</div>
        </div>
      ),
    },
    {
      key: 'memberCount',
      label: 'Members',
      sortable: true,
      render: (g) => <span className="text-sm text-gray-700">{g.memberCount}</span>,
    },
    {
      key: 'ruleCount',
      label: 'In use',
      sortable: true,
      render: (g) =>
        g.ruleCount > 0 ? (
          <span className="inline-flex items-center rounded bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            {g.ruleCount} rule{g.ruleCount === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="text-gray-400">—</span>
        ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (g) => (
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
            title="Manage members"
            onClick={() => void openMembers(g)}
          >
            <UsersIcon className="w-4 h-4" /> Members
          </button>
          <button
            type="button"
            className="text-gray-400 hover:text-danger-600 disabled:cursor-not-allowed disabled:opacity-40"
            title={g.ruleCount > 0 ? `Referenced by ${g.ruleCount} policy rule(s) — remove it from them first` : 'Delete group'}
            disabled={g.ruleCount > 0}
            onClick={() => setDeleteTarget(g)}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ];

  const memberIds = new Set(members.map((m) => m.vpnUserId));
  const q = memberQuery.trim().toLowerCase();
  const candidates = directory.filter(
    (u) =>
      !memberIds.has(u.id) &&
      (!q || u.email.toLowerCase().includes(q) || (u.displayName ?? '').toLowerCase().includes(q)),
  );
  const totalMembers = groups.reduce((n, g) => n + g.memberCount, 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">User Groups</h1>
          <p className="text-gray-600 mt-1">
            Group this tenant's users for Secure Access policy — a rule's <span className="font-medium">Who → Groups</span> matches these.
          </p>
        </div>
        <div className="flex space-x-2">
          <button type="button" className="btn-secondary" onClick={() => void fetchGroups()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 inline ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" className="btn-primary" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-2 inline" />
            Add Group
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">{error}</div>
      )}

      <div className="grid grid-cols-3 gap-4">
        <StatCard title="Groups" value={groups.length} icon={UsersIcon} color="primary" loading={loading} />
        <StatCard title="Memberships" value={totalMembers} icon={UsersIcon} color="success" loading={loading} />
        <StatCard title="Used by policies" value={groups.filter((g) => g.ruleCount > 0).length} icon={UsersIcon} color="info" loading={loading} />
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={groups}
          loading={loading}
          searchPlaceholder="Search groups…"
          emptyMessage="No user groups yet — add one to reference it in a policy rule"
        />
      </div>

      {/* Create */}
      <Modal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        title="Add User Group"
        size="md"
        footer={
          <div className="flex justify-end space-x-2">
            <button type="button" className="btn-secondary" onClick={() => setIsCreateOpen(false)} disabled={creating}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={() => void handleCreate()} disabled={creating}>
              {creating ? 'Creating…' : 'Create Group'}
            </button>
          </div>
        }
      >
        <AccessFormField
          label="Name"
          htmlFor="group-name"
          required
          error={newNameError ?? undefined}
          hint="Unique within this tenant only — other tenants may use the same name."
        >
          <input
            id="group-name"
            className="input-base w-full"
            placeholder="engineering"
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setNewNameError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleCreate();
            }}
          />
        </AccessFormField>
      </Modal>

      {/* Members */}
      <Modal
        isOpen={!!membersFor}
        onClose={() => setMembersFor(null)}
        title={membersFor ? `Members — ${membersFor.name}` : 'Members'}
        footer={
          <div className="flex justify-end">
            <button type="button" className="btn-secondary" onClick={() => setMembersFor(null)}>
              Close
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Current members</p>
            {membersLoading ? (
              <p className="text-sm text-gray-400">Loading…</p>
            ) : members.length === 0 ? (
              <p className="text-sm text-gray-400">No members yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded border border-gray-200">
                {members.map((m) => (
                  <li key={m.vpnUserId} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <div className="text-gray-900">{m.displayName || m.email}</div>
                      <div className="text-xs text-gray-500">{m.email}</div>
                    </div>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-danger-600 disabled:opacity-40"
                      title="Remove from group"
                      disabled={busyUserId === m.vpnUserId}
                      onClick={() => void removeMember(m)}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Add a user from this tenant</p>
            <input
              className="input-base w-full mb-2"
              placeholder="Search users by name or email…"
              value={memberQuery}
              onChange={(e) => setMemberQuery(e.target.value)}
            />
            <div className="max-h-48 overflow-auto rounded border border-gray-200">
              {candidates.length === 0 ? (
                <p className="p-3 text-sm text-gray-400">
                  {directory.length === 0 ? 'No users in this tenant yet.' : 'No matching users (or all are already members).'}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {candidates.map((u) => (
                    <li key={u.id} className="flex items-center justify-between px-3 py-2 text-sm">
                      <div>
                        <div className="text-gray-900">{u.displayName || u.email}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 disabled:opacity-40"
                        disabled={busyUserId === u.id || !u.keycloakSub}
                        title={u.keycloakSub ? 'Add to group' : 'User has not activated yet'}
                        onClick={() => void addMember(u)}
                      >
                        <UserPlus className="w-3.5 h-3.5" /> Add
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">External groups (identity provider)</p>
            {providers.length === 0 ? (
              <p className="text-sm text-gray-400">
                No external identity provider configured — add one under{' '}
                <Link to="/admin/identity-providers" className="text-primary-600 hover:underline">Identity Providers</Link>{' '}
                to map IdP groups onto this group.
              </p>
            ) : (
              <div className="space-y-2">
                {mappings.length > 0 && (
                  <ul className="divide-y divide-gray-100 rounded border border-gray-200">
                    {mappings.map((m) => (
                      <li key={m.id} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div>
                          <div className="font-mono text-xs text-gray-900">{m.externalGroupId}</div>
                          <div className="text-xs text-gray-500">{m.identityProviderName} · claim <span className="font-mono">{m.claim}</span></div>
                        </div>
                        <button type="button" className="text-gray-400 hover:text-danger-600 disabled:opacity-40" title="Unlink" disabled={mappingBusy} onClick={() => void removeMapping(m)}>
                          <X className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-center gap-2">
                  {providers.length > 1 && (
                    <select className="input-base w-44" value={mappingProviderId} onChange={(e) => setMappingProviderId(e.target.value)}>
                      {providers.map((p) => (
                        <option key={p.id} value={p.id}>{p.displayName}</option>
                      ))}
                    </select>
                  )}
                  <input
                    className="input-base flex-1 font-mono"
                    placeholder="IdP group id (Entra: group object ID)"
                    value={externalGroupId}
                    onChange={(e) => setExternalGroupId(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void addMapping();
                    }}
                  />
                  <button type="button" className="btn-secondary text-sm inline-flex items-center gap-1" disabled={mappingBusy || !externalGroupId.trim() || !mappingProviderId} onClick={() => void addMapping()}>
                    <Link2 className="w-3.5 h-3.5" /> Link
                  </button>
                </div>
                <p className="text-xs text-gray-500">Users whose IdP <span className="font-mono">groups</span> claim contains a linked id join this group on every sign-in.</p>
              </div>
            )}
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete user group"
        message={`Delete '${deleteTarget?.name}'? Members keep their accounts; only the group goes away.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default UserGroups;
